import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import clsx from "clsx";
import { useId, useRef, useState } from "react";
import { getClosestLayer } from "@/utils/dom";
import { Btn } from "../btn/Btn";
import { Icon, type IconProps } from "../icon/Icon";
import styles from "./Input.module.css";

type InputSize = "xs" | "sm" | "md" | "lg" | "xl";

type InputColor = "grey" | "white" | "black";

interface InputBaseProps<Value extends string | number> {
  ref?: React.RefObject<HTMLDivElement | null>;
  id?: string;
  defaultValue?: string | number;
  value?: Value;
  placeholder?: string;
  name?: string;
  error?: Error;
  errorVisible?: boolean;
  className?: string;
  icon?: IconProps["name"] | IconProps;
  size?: InputSize;
  color?: InputColor;
  clear?: boolean;
  search?: boolean;
  go?: string;
  disabled?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  enterKeyHint?:
    | "enter"
    | "done"
    | "go"
    | "next"
    | "previous"
    | "search"
    | "send";
  autoComplete?: "on" | "off";
  autoFocus?: boolean;
  attraction?: "blink";
  dataList?: (value: Value, close: () => void) => React.ReactNode;
  onChange?: (value: Value) => void;
  onFocus?: (value: Value) => void;
  onBlur?: (value: Value) => void;
  onSubmit?: (value: Value) => void;
  onClear?: (value: Value) => void;
  onSearch?: (value: Value) => void;
  onGo?: (value: Value) => void;
}

interface InputTextProps extends InputBaseProps<string> {
  type?: "text" | "email" | "password" | "textarea" | "file";
  inputMode?: "text" | "tel" | "url" | "email" | "search" | "numeric";
  rows?: never;
}

interface InputTextAreaProps extends InputBaseProps<string> {
  type: "textarea";
  rows?: number;
  inputMode?: never;
}

interface InputNumberProps extends InputBaseProps<number> {
  type: "number";
  rows?: never;
  inputMode?: never;
}

export type InputProps = InputTextProps | InputNumberProps;

export function Input(props: InputTextProps): React.ReactNode;
export function Input(props: InputTextAreaProps): React.ReactNode;
export function Input(props: InputNumberProps): React.ReactNode;
export function Input(
  props: InputTextProps | InputTextAreaProps | InputNumberProps,
): React.ReactNode {
  const {
    ref,
    id: idProp,
    placeholder,
    name,
    error,
    errorVisible = true,
    className,
    icon,
    size = "md",
    color = "white",
    clear = false,
    search = false,
    go,
    disabled: disabledProp = false,
    loading: loadingProp = false,
    readOnly = false,
    enterKeyHint,
    autoComplete = "off",
    autoFocus,
    attraction,
  } = props;

  const id = useId();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [lastError, setLastError] = useState<Error>();

  const controls =
    clear || props.type === "password" || search || go !== undefined;

  const controlSize =
    size === "xl" ? "md" : size === "lg" ? "sm" : size === "xs" ? "2xs" : "xs";

  const disabled = loadingProp || disabledProp;

  const loading =
    (loadingProp === true && search === true) ||
    (loadingProp === true && go !== undefined)
      ? false
      : loadingProp;

  const getNumericValue = (input: HTMLInputElement | null) => {
    if (input === null) {
      return 0;
    }

    return Number.isNaN(input.valueAsNumber)
      ? 0
      : Math.trunc(input.valueAsNumber);
  };

  const onChangeHandler = () => {
    if (props.type === "number") {
      if (inputRef.current instanceof HTMLInputElement) {
        props.onChange?.(getNumericValue(inputRef.current));
      }
    } else {
      props.onChange?.(inputRef.current?.value ?? "");
    }
  };

  const onFocusHandler = () => {
    setLastError(error);

    if (props.type === "number") {
      if (inputRef.current instanceof HTMLInputElement) {
        props.onFocus?.(getNumericValue(inputRef.current));
      }
    } else {
      props.onFocus?.(inputRef.current?.value ?? "");
    }

    if (props.dataList !== undefined) {
      setOpen(true);
    }
  };

  const onBlurHandler = () => {
    if (props.type === "number") {
      if (inputRef.current instanceof HTMLInputElement) {
        props.onBlur?.(getNumericValue(inputRef.current));
      }
    } else {
      props.onBlur?.(inputRef.current?.value ?? "");
    }
  };

  const onSubmitHandler: React.KeyboardEventHandler = (event) => {
    if (event.code === "Enter" || event.key === "Enter") {
      if (props.type === "number") {
        if (inputRef.current instanceof HTMLInputElement) {
          props.onSubmit?.(getNumericValue(inputRef.current));
        }
      } else {
        props.onSubmit?.(inputRef.current?.value ?? "");
      }
      setOpen(false);
    }
  };

  const onClearHandler = () => {
    if (props.type === "number") {
      props.onChange?.(0);
      props.onClear?.(0);
    } else {
      props.onChange?.("");
      props.onClear?.("");
    }

    if (inputRef.current instanceof HTMLInputElement) {
      inputRef.current.value = "";
    }

    if (props.type === "file" && inputRef.current instanceof HTMLInputElement) {
      inputRef.current.type = "text";
      inputRef.current.type = "file";
    }

    inputRef.current?.focus();
  };

  const onPasswordToggleHandler = () => {
    if (inputRef.current instanceof HTMLInputElement) {
      if (inputRef.current.type === "password") {
        inputRef.current.type = "text";
      } else {
        inputRef.current.type = "password";
      }
    }
  };

  const onSearchHandler = () => {
    if (props.type === "number") {
      props.onSearch?.(props.value ?? 0);
    } else {
      props.onSearch?.(props.value ?? "");
    }
  };

  const onGoHandler = () => {
    if (props.type === "number") {
      props.onGo?.(props.value ?? 0);
    } else {
      props.onGo?.(props.value ?? "");
    }
  };

  const [open, setOpen] = useState(false);

  const floating = useFloating<HTMLElement>({
    open,
    onOpenChange: setOpen,
    strategy: "fixed",
    middleware: [offset(12), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const interactions = useInteractions([useDismiss(floating.context)]);

  const dataListContent =
    props.type === "number"
      ? props.dataList?.(props.value ?? 0, () => {
          setOpen(false);
        })
      : props.dataList?.(props.value ?? "", () => {
          setOpen(false);
        });

  return (
    <>
      <div
        ref={ref}
        className={clsx(
          styles.input,
          [
            styles[`input-color-${color}`],
            styles[`input-size-${size}`],
            error !== undefined &&
              error !== lastError &&
              styles[`input-invalid`],
            disabled === true && styles[`input-disabled`],
            loading === true && styles[`input-loading`],
            attraction !== undefined &&
              styles[`input-attraction-${attraction}`],
          ],
          className,
        )}
      >
        <div
          ref={floating.refs.setReference}
          className={styles["input-container"]}
        >
          {icon !== undefined &&
            (loading === true ? (
              <Icon
                className={clsx(
                  styles["input-icon"],
                  styles["input-loading-icon"],
                )}
                name="loader_24px_regular"
                animation="spin"
              />
            ) : (
              <InputIcon className={styles["input-icon"]} icon={icon} />
            ))}

          {props.type !== "textarea" && (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement | null>}
              id={idProp ?? id}
              className={styles["input-element"]}
              type={props.type}
              defaultValue={props.defaultValue}
              value={props.value}
              name={name}
              placeholder={
                size === "lg" || size === "xl" ? " " : (placeholder ?? " ")
              }
              readOnly={readOnly === true || loading === true}
              disabled={disabled === true}
              inputMode={props.inputMode}
              enterKeyHint={enterKeyHint}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              onChange={onChangeHandler}
              onFocus={onFocusHandler}
              onBlur={onBlurHandler}
              onKeyDown={onSubmitHandler}
              aria-labelledby={`label-${idProp ?? id}`}
            />
          )}

          {props.type === "textarea" && (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement | null>}
              id={idProp ?? id}
              className={styles["input-element"]}
              defaultValue={props.defaultValue}
              value={props.value}
              name={name}
              placeholder={placeholder ?? " "}
              rows={props.rows}
              readOnly={readOnly === true || loading === true}
              disabled={disabled === true}
              onChange={onChangeHandler}
              onKeyDown={onSubmitHandler}
              onFocus={onFocusHandler}
              aria-labelledby={`label-${idProp ?? id}`}
            />
          )}

          {controls && (
            <div className={styles["input-controls"]}>
              {props.type === "password" && (
                <>
                  <Btn
                    htmlFor={idProp ?? id}
                    className={clsx(
                      styles["input-control"],
                      styles["input-control-password-hidden"],
                    )}
                    icon="visibility_off_24px_regular"
                    size={controlSize}
                    mode="clear"
                    disabled={disabled || loading}
                    tabIndex={-1}
                    onClick={onPasswordToggleHandler}
                  />
                  <Btn
                    htmlFor={idProp ?? id}
                    className={clsx(
                      styles["input-control"],
                      styles["input-control-password-visible"],
                    )}
                    icon="visibility_24px_regular"
                    size={controlSize}
                    mode="clear"
                    disabled={disabled || loading}
                    tabIndex={-1}
                    onClick={onPasswordToggleHandler}
                  />
                </>
              )}

              {clear === true && (
                <Btn
                  htmlFor={idProp ?? id}
                  className={clsx(
                    styles["input-control"],
                    styles["input-control-clear"],
                  )}
                  icon="cross_24px_regular"
                  size={controlSize}
                  mode="clear"
                  disabled={disabled || loading}
                  tabIndex={-1}
                  onClick={onClearHandler}
                />
              )}

              {search === true && (
                <Btn
                  className={clsx(
                    styles["input-control"],
                    styles["input-control-search"],
                  )}
                  icon="search_24px_regular"
                  color="primary"
                  size={controlSize}
                  disabled={disabled || loading}
                  onClick={onSearchHandler}
                />
              )}

              {go !== undefined && (
                <Btn
                  className={clsx(
                    styles["input-control"],
                    styles["input-control-go"],
                  )}
                  text={go}
                  endIcon={
                    loadingProp
                      ? {
                          name: "loader_24px_regular",
                          animation: "spin",
                        }
                      : "arrow_right_long_24px_regular"
                  }
                  color="primary"
                  size={controlSize}
                  disabled={disabled || loading}
                  onClick={onGoHandler}
                />
              )}
            </div>
          )}

          {(size === "lg" || size === "xl") && placeholder !== undefined && (
            <div
              id={`label-${idProp ?? id}`}
              className={clsx(styles["input-custom-placeholder"])}
              aria-hidden="true"
            >
              {placeholder}
            </div>
          )}
        </div>

        {error !== undefined && errorVisible && <InputError error={error} />}
      </div>

      {open === true && props.dataList !== undefined && (
        <FloatingPortal root={getClosestLayer(floating.elements.domReference)}>
          <div
            ref={floating.refs.setFloating}
            className={styles["input-data-list-floating"]}
            style={{
              ...floating.floatingStyles,
              width: floating.refs.domReference.current?.clientWidth,
            }}
            {...interactions.getFloatingProps()}
          >
            <div className={styles["input-data-list"]}>{dataListContent}</div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

const InputIcon: React.FC<{
  icon: IconProps["name"] | IconProps;
  className?: string;
}> = ({ icon, className }) => {
  if (typeof icon === "string") {
    return <Icon className={className} name={icon} />;
  }

  return <Icon {...icon} className={clsx(className, icon.className)} />;
};

export const InputError: React.FC<{
  className?: string;
  error?: Error;
}> = ({ className, error }) => {
  if (error === undefined) {
    return null;
  }

  return (
    <div className={clsx(styles["input-error"], className)}>
      {error.message}
    </div>
  );
};
